import React, { useState, useEffect, useCallback } from 'react';
import {
    DollarSign, TrendingUp, Clock, CheckCircle2, MousePointerClick, ShoppingCart,
    Upload, Loader2, X, RefreshCw, AlertTriangle, Info
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../config/apiBase';

const PERIODS = [[7, '7 dias'], [30, '30 dias'], [90, '90 dias']];

// Metrica que o backend nao consegue calcular com confianca volta null.
// A tela mostra "—" nesses casos, nunca R$ 0,00 fingindo que foi medido.
const money = (v) => v === null || v === undefined
    ? '—'
    : `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (v) => v === null || v === undefined ? '—' : Number(v).toLocaleString('pt-BR');
const pct = (v) => v === null || v === undefined ? '—' : `${Number(v).toFixed(2)}%`;

export default function ComissoesPage() {
    const [days, setDays] = useState(30);
    const [custom, setCustom] = useState({ start: '', end: '' });
    const [marketplace, setMarketplace] = useState('');
    const [summary, setSummary] = useState(null);
    const [breakdowns, setBreakdowns] = useState({});
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showImport, setShowImport] = useState(false);

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    const periodQuery = useCallback(() => {
        const p = new URLSearchParams();
        if (custom.start && custom.end) {
            p.set('start', new Date(custom.start).toISOString());
            p.set('end', new Date(custom.end + 'T23:59:59').toISOString());
        } else {
            p.set('days', String(days));
        }
        if (marketplace) p.set('marketplace', marketplace);
        return p.toString();
    }, [days, custom, marketplace]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const qs = periodQuery();
            const [s, ...bs] = await Promise.all([
                fetch(`${API}/commissions/summary?${qs}`, { headers }).then(r => r.json()),
                ...['day', 'marketplace', 'category', 'offer'].map(by =>
                    fetch(`${API}/commissions/breakdown?by=${by}&${qs}`, { headers }).then(r => r.json())),
            ]);
            setSummary(s);
            setBreakdowns({ day: bs[0], marketplace: bs[1], category: bs[2], offer: bs[3] });
        } catch {
            toast.error('Erro ao carregar comissões');
        } finally {
            setLoading(false);
        }
    }, [periodQuery]);

    const loadProviders = useCallback(async () => {
        try {
            const r = await fetch(`${API}/commissions/providers`, { headers });
            const d = await r.json();
            setProviders(d.providers || []);
        } catch { /* silencioso: a tela funciona sem isso */ }
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadProviders(); }, [loadProviders]);

    const syncProvider = async (mk) => {
        toast.info(`Buscando comissões na API ${mk}...`);
        try {
            const r = await fetch(`${API}/commissions/sync/${mk}?days=${days}`, { method: 'POST', headers });
            const d = await r.json();
            if (!r.ok) { toast.error(d.detail || 'Erro na importação'); return; }
            toast.success(`${d.created} nova(s), ${d.updated} atualizada(s), ${d.unchanged} sem mudança`);
            load();
        } catch { toast.error('Erro de conexão'); }
    };

    const cards = [
        { label: 'Comissão total', value: money(summary?.commission_total), icon: DollarSign, color: 'var(--sv-gold-primary)' },
        { label: 'Aprovada', value: money(summary?.commission_approved), icon: CheckCircle2, color: '#22c55e' },
        { label: 'Pendente', value: money(summary?.commission_pending), icon: Clock, color: '#f59e0b' },
        { label: 'Vendas', value: num(summary?.sales_count), icon: ShoppingCart, color: 'var(--text)' },
        { label: 'Cliques', value: num(summary?.clicks), icon: MousePointerClick, color: 'var(--text)' },
        { label: 'Conversão', value: pct(summary?.conversion_rate), icon: TrendingUp, color: 'var(--text)' },
        { label: 'EPC', value: summary?.epc == null ? '—' : `R$ ${Number(summary.epc).toFixed(4)}`, icon: DollarSign, color: 'var(--text)' },
    ];

    // Qualidade da atribuicao derivada do breakdown REAL, nao do rotulo.
    // O bug anterior: o aviso so aparecia com attribution === 'aggregate', entao a
    // primeira venda exata mudava o rotulo pra 'mixed' e o alerta sumia sozinho,
    // mesmo com o resto das vendas ainda agregado.
    const ab = summary?.attribution_breakdown || null;
    const nExact = Number(ab?.exact || 0);
    const nInferred = Number(ab?.inferred || 0);
    const nAggregate = Number(ab?.aggregate || 0);
    const nAtrib = nExact + nInferred + nAggregate;
    const temNaoExata = nInferred > 0 || nAggregate > 0;
    // percentual so existe com denominador > 0: 0 vendas nunca vira "100% exata"
    const pctExata = nAtrib > 0 ? (nExact / nAtrib) * 100 : null;
    const tudoExato = nAtrib > 0 && nExact === nAtrib;

    let avisoAtrib = null;
    if (nAtrib === 0) {
        avisoAtrib = null;                      // sem vendas: nao afirma nada
    } else if (nExact > 0 && temNaoExata) {
        avisoAtrib = 'A atribuicao e mista: parte das vendas esta vinculada ao clique exato e parte ainda e agregada ou inferida.';
    } else if (temNaoExata) {
        avisoAtrib = 'Parte das vendas ainda nao possui atribuicao exata por clique.';
    }

    const apiProviders = providers.filter(p => p.mode === 'api' && p.available);

    return (
        <div className="sv-page" data-testid="comissoes-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="text-lg font-bold" style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}>Comissões</h2>
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>Quanto o Super Vitrine faturou de comissão</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {apiProviders.map(p => (
                        <button key={p.marketplace} className="sv-btn-system flex items-center gap-1.5"
                            onClick={() => syncProvider(p.marketplace)} data-testid={`sync-${p.marketplace}`}>
                            <RefreshCw size={13} /> Sincronizar {p.label}
                        </button>
                    ))}
                    <button className="sv-btn-gold-system sv-btn-system" onClick={() => setShowImport(true)} data-testid="import-btn">
                        <Upload size={14} /> Importar relatório
                    </button>
                </div>
            </div>

            {/* Periodo */}
            <div className="sv-card-system mb-3">
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex gap-2">
                        {PERIODS.map(([d, l]) => (
                            <button key={d} className={`sv-subnav-chip ${!custom.start && days === d ? 'active' : ''}`}
                                onClick={() => { setCustom({ start: '', end: '' }); setDays(d); }}>{l}</button>
                        ))}
                    </div>
                    <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--muted)' }}>
                        <span>Personalizado:</span>
                        <input type="date" value={custom.start} onChange={e => setCustom({ ...custom, start: e.target.value })}
                            className="px-2 py-1 rounded-lg text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} />
                        <span>até</span>
                        <input type="date" value={custom.end} onChange={e => setCustom({ ...custom, end: e.target.value })}
                            className="px-2 py-1 rounded-lg text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} />
                    </div>
                    <select value={marketplace} onChange={e => setMarketplace(e.target.value)}
                        className="px-2 py-1.5 rounded-lg text-xs ml-auto"
                        style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }}>
                        <option value="">Todas as lojas</option>
                        {providers.map(p => <option key={p.marketplace} value={p.marketplace}>{p.label}</option>)}
                    </select>
                </div>
            </div>

            {/* Cards */}
            <div className="sv-kpi-grid" data-testid="comissoes-kpis">
                {cards.map(c => (
                    <div className="sv-kpi" key={c.label}>
                        <span className="sv-kpi-label flex items-center gap-1.5"><c.icon size={11} /> {c.label}</span>
                        <span className="sv-kpi-value" style={{ color: c.color }}>{loading ? '...' : c.value}</span>
                    </div>
                ))}
            </div>

            {/* Qualidade da atribuicao */}
            {summary && !loading && nAtrib > 0 && (
                <div className="sv-card-system" data-testid="comissoes-atribuicao">
                    <div className="flex items-center gap-1.5 mb-2">
                        <Info size={13} style={{ color: 'var(--muted)' }} />
                        <span className="text-xs font-semibold" style={{ color: 'var(--text)' }}>Qualidade da atribuicao</span>
                        {pctExata !== null && (
                            <span className="text-xs" style={{ color: 'var(--muted)' }}>
                                &middot; {pctExata.toFixed(0)}% das vendas com atribuicao exata
                            </span>
                        )}
                    </div>
                    <div className="sv-kpi-grid">
                        <div className="sv-kpi">
                            <span className="sv-kpi-label">Exata</span>
                            <span className="sv-kpi-value" style={{ color: '#22c55e' }}>{num(nExact)}</span>
                        </div>
                        <div className="sv-kpi">
                            <span className="sv-kpi-label">Inferida</span>
                            <span className="sv-kpi-value" style={{ color: '#f59e0b' }}>{num(nInferred)}</span>
                        </div>
                        <div className="sv-kpi">
                            <span className="sv-kpi-label">Agregada</span>
                            <span className="sv-kpi-value" style={{ color: 'var(--muted)' }}>{num(nAggregate)}</span>
                        </div>
                        {ab?.commission_exact != null && (
                            <div className="sv-kpi">
                                <span className="sv-kpi-label">Comissao atribuida</span>
                                <span className="sv-kpi-value" style={{ color: '#22c55e' }}>{money(ab.commission_exact)}</span>
                            </div>
                        )}
                        {ab?.commission_unattributed != null && (
                            <div className="sv-kpi">
                                <span className="sv-kpi-label">Sem atribuicao exata</span>
                                <span className="sv-kpi-value" style={{ color: 'var(--muted)' }}>{money(ab.commission_unattributed)}</span>
                            </div>
                        )}
                    </div>
                    {tudoExato && (
                        <p className="text-xs" style={{ color: '#22c55e', margin: '8px 0 0' }}>
                            Todas as conversoes deste periodo possuem atribuicao exata por clique.
                        </p>
                    )}
                </div>
            )}

            {/* Aviso honesto: depende do breakdown real, nao do rotulo 'attribution' */}
            {avisoAtrib && !loading && (
                <div className="sv-card-system flex items-start gap-2.5" style={{ borderColor: 'rgba(245,158,11,0.3)' }}>
                    <Info size={15} style={{ color: '#f59e0b', flexShrink: 0, marginTop: 2 }} />
                    <p className="text-xs" style={{ color: 'var(--muted)', margin: 0 }}>
                        {avisoAtrib}{summary?.attribution_note ? ' ' + summary.attribution_note : ''}
                    </p>
                </div>
            )}

            {summary && summary.items_count === 0 && !loading && (
                <div className="sv-empty-state">
                    <AlertTriangle style={{ color: 'var(--muted-2)' }} />
                    <p className="sv-empty-state-title">Nenhuma comissão registrada nesse período</p>
                    <p className="sv-empty-state-desc">
                        Importe o relatório do programa de afiliado, ou sincronize as lojas que têm API disponível.
                    </p>
                </div>
            )}

            {/* Tabelas */}
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
                <BreakdownTable title="Comissão por dia" data={breakdowns.day} keyLabel="Dia" />
                <BreakdownTable title="Comissão por loja" data={breakdowns.marketplace} keyLabel="Loja" />
                <BreakdownTable title="Comissão por categoria" data={breakdowns.category} keyLabel="Categoria" />
                <BreakdownTable title="Top ofertas por comissão" data={breakdowns.offer} keyLabel="Oferta" />
            </div>

            {showImport && (
                <ImportModal providers={providers} onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); }} />
            )}
        </div>
    );
}

function BreakdownTable({ title, data, keyLabel }) {
    const rows = data?.rows || [];
    return (
        <div className="sv-card-system" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="px-5 py-3" style={{ borderBottom: '1px solid var(--border)' }}>
                <span className="sv-card-label">{title}</span>
            </div>
            {rows.length === 0 ? (
                <div className="px-5 py-6 text-xs" style={{ color: 'var(--muted-2)' }}>Sem dados no período</div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="sv-data-table">
                        <thead><tr><th>{keyLabel}</th><th style={{ textAlign: 'right' }}>Comissão</th><th style={{ textAlign: 'right' }}>Vendas</th></tr></thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr key={i}>
                                    <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String(r.key ?? '—')}</td>
                                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--sv-gold-primary)' }}>
                                        R$ {Number(r.commission).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                    </td>
                                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--muted)' }}>{r.orders}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function ImportModal({ providers, onClose, onDone }) {
    const [marketplace, setMarketplace] = useState('');
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [mapping, setMapping] = useState({});
    const [busy, setBusy] = useState(false);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    const doPreview = async (f) => {
        setFile(f); setPreview(null);
        const fd = new FormData();
        fd.append('file', f);
        fd.append('marketplace', marketplace);
        setBusy(true);
        try {
            const r = await fetch(`${API}/commissions/import/preview`, { method: 'POST', headers, body: fd });
            const d = await r.json();
            if (!r.ok) { toast.error(d.detail || 'Erro ao ler arquivo'); return; }
            setPreview(d);
            setMapping(d.suggested_mapping || {});
        } catch { toast.error('Erro de conexão'); } finally { setBusy(false); }
    };

    const doImport = async () => {
        if (!marketplace) { toast.error('Escolha a loja do relatório'); return; }
        const fd = new FormData();
        fd.append('file', file);
        fd.append('marketplace', marketplace);
        fd.append('mapping', JSON.stringify(mapping));
        setBusy(true);
        try {
            const r = await fetch(`${API}/commissions/import`, { method: 'POST', headers, body: fd });
            const d = await r.json();
            if (!r.ok) { toast.error(d.detail || 'Erro na importação'); return; }
            toast.success(`${d.created} nova(s) · ${d.updated} atualizada(s) · ${d.unchanged} sem mudança · ${d.skipped} ignorada(s)`);
            onDone();
        } catch { toast.error('Erro de conexão'); } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="sv-card-system w-full max-w-2xl" style={{ maxHeight: '88vh', overflowY: 'auto' }} data-testid="import-modal">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-bold" style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}>Importar relatório de comissão</h3>
                    <button className="sv-btn-system" style={{ padding: 4 }} onClick={onClose}><X size={14} /></button>
                </div>

                <div className="flex flex-col gap-3">
                    <div>
                        <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>1. Loja do relatório</label>
                        <select value={marketplace} onChange={e => setMarketplace(e.target.value)}
                            className="w-full px-3 py-2.5 rounded-lg text-sm"
                            style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }}>
                            <option value="">Selecione...</option>
                            {providers.map(p => <option key={p.marketplace} value={p.marketplace}>{p.label}</option>)}
                        </select>
                    </div>

                    <div>
                        <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>2. Arquivo CSV exportado do painel</label>
                        <input type="file" accept=".csv,text/csv" onChange={e => e.target.files[0] && doPreview(e.target.files[0])}
                            className="w-full text-xs" style={{ color: 'var(--muted)' }} data-testid="import-file" />
                        <p className="text-[11px] mt-1" style={{ color: 'var(--muted-2)' }}>
                            XLSX ainda não é aceito — exporte como CSV no painel do afiliado.
                        </p>
                    </div>

                    {busy && <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--muted)' }}><Loader2 size={13} className="animate-spin" /> Processando...</div>}

                    {preview && (
                        <>
                            <div>
                                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>
                                    3. Conferir colunas ({preview.total_rows} linhas no arquivo)
                                </label>
                                <div className="flex flex-col gap-1.5">
                                    {preview.mappable_fields.map(f => (
                                        <div key={f} className="flex items-center gap-2">
                                            <span className="text-[11px] font-mono" style={{ color: 'var(--muted)', width: 170 }}>{f}</span>
                                            <select value={mapping[f] || ''} onChange={e => setMapping({ ...mapping, [f]: e.target.value })}
                                                className="flex-1 px-2 py-1 rounded text-xs"
                                                style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }}>
                                                <option value="">— não usar —</option>
                                                {preview.headers.map(h => <option key={h} value={h}>{h}</option>)}
                                            </select>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>4. Prévia</label>
                                <div className="overflow-x-auto">
                                    <table className="sv-data-table" style={{ fontSize: 12 }}>
                                        <thead><tr><th>Produto</th><th>Pedido</th><th>Venda</th><th>Comissão</th><th>Status</th></tr></thead>
                                        <tbody>
                                            {preview.preview.map((p, i) => (
                                                <tr key={i}>
                                                    <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.product_name || '—'}</td>
                                                    <td>{p.external_order_id || '—'}</td>
                                                    <td>{p.sale_amount == null ? '—' : `R$ ${p.sale_amount}`}</td>
                                                    <td>{p.commission_amount == null ? '—' : `R$ ${p.commission_amount}`}</td>
                                                    <td>{p.commission_status}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <p className="text-[11px] mt-1.5" style={{ color: 'var(--muted-2)' }}>
                                    Coluna não mapeada fica vazia — nenhum valor é inventado. Importar o mesmo arquivo duas vezes não duplica.
                                </p>
                            </div>
                        </>
                    )}
                </div>

                <div className="flex justify-end gap-2 mt-5">
                    <button className="sv-btn-system" onClick={onClose}>Cancelar</button>
                    <button className="sv-btn-gold-system sv-btn-system" onClick={doImport}
                        disabled={busy || !preview || !marketplace} data-testid="confirm-import">
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Importar
                    </button>
                </div>
            </div>
        </div>
    );
}
